import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Play, X, CheckCircle, XCircle, Clock, GitBranch, FunnelSimple, ChartBar } from '@phosphor-icons/react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatUtcToIst, formatUtcToIstDate } from '../modules/tickets/utils/datetime';
import { ModuleShell } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
const API = apiPath('/api');

function TestExecutionDashboard() {
  const { user } = useAuth();
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [filters, setFilters] = useState({
    test_type: 'all',
    status: 'all',
    environment: 'all'
  });
  
  const [formData, setFormData] = useState({
    test_type: 'unit',
    environment: 'local',
    total_tests: 0,
    passed: 0,
    failed: 0,
    skipped: 0,
    duration_seconds: 0,
    test_results: {}
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      };

      const params = {};
      if (filters.test_type !== 'all') params.test_type = filters.test_type;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.environment !== 'all') params.environment = filters.environment;

      const [executionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/test-executions`, { ...config, params }),
        axios.get(`${API}/test-executions/stats/summary`, config)
      ]);

      setExecutions(executionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch test execution data:', error);
      if (error.response?.status === 403) {
        alert('Access denied - SuperAdmin only');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExecution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const status = formData.failed === 0 ? 'passed' : 'failed';
      
      const payload = {
        ...formData,
        status,
        test_results: {
          summary: {
            total: formData.total_tests,
            passed: formData.passed,
            failed: formData.failed,
            skipped: formData.skipped
          },
          timestamp: new Date().toISOString(),
          details: 'Manual test execution entry'
        }
      };

      await axios.post(`${API}/test-executions`, payload, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowCreateModal(false);
      setFormData({
        test_type: 'unit',
        environment: 'local',
        total_tests: 0,
        passed: 0,
        failed: 0,
        skipped: 0,
        duration_seconds: 0,
        test_results: {}
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create test execution:', error);
      alert('Failed to create test execution');
    }
  };

  if (loading) {
    return <div style={{ padding: '12px' }}>Loading...</div>;
  }

  const passRateData = stats ? [
    { name: 'Passed', value: stats.passed_executions, color: '#10B981' },
    { name: 'Failed', value: stats.failed_executions, color: '#EF4444' }
  ] : [];

  const testTypesData = stats?.test_types_breakdown?.map(t => ({
    name: t._id,
    total: t.count,
    passed: t.passed,
    failed: t.count - t.passed
  })) || [];

  return (
    <ModuleShell
      title="Test Executions"
      subtitle="Track and monitor all test executions • SuperAdmin Only"
      headerActions={(
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-execution-button"
        >
          <Play size={18} weight="bold" />
          Log Test Execution
        </button>
      )}
    >
    <div data-testid="test-execution-dashboard">

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <div className="stat-card" data-testid="total-executions-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>
                  Total Executions
                </p>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{stats.total_executions}</p>
              </div>
              <div style={{ background: '#002FA7', padding: '12px', borderRadius: '8px' }}>
                <Play size={24} color="white" weight="bold" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="passed-executions-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>
                  Passed
                </p>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#10B981' }}>{stats.passed_executions}</p>
              </div>
              <div style={{ background: '#10B981', padding: '12px', borderRadius: '8px' }}>
                <CheckCircle size={24} color="white" weight="bold" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="failed-executions-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>
                  Failed
                </p>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#EF4444' }}>{stats.failed_executions}</p>
              </div>
              <div style={{ background: '#EF4444', padding: '12px', borderRadius: '8px' }}>
                <XCircle size={24} color="white" weight="bold" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="pass-rate-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>
                  Pass Rate
                </p>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{stats.pass_rate}%</p>
              </div>
              <div style={{ background: '#8B5CF6', padding: '12px', borderRadius: '8px' }}>
                <ChartBar size={24} color="white" weight="bold" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
          <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '12px' }}>
            <h2 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Pass/Fail Distribution</h2>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={passRateData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {passRateData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '12px' }}>
            <h2 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Test Types Breakdown</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={testTypesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="passed" fill="#10B981" name="Passed" />
                <Bar dataKey="failed" fill="#EF4444" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <FunnelSimple size={20} weight="bold" />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Filters</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Test Type</label>
            <select
              value={filters.test_type}
              onChange={(e) => setFilters({ ...filters, test_type: e.target.value })}
              className="input-field"
              style={{ padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="all">All Types</option>
              <option value="unit">Unit Tests</option>
              <option value="integration">Integration Tests</option>
              <option value="e2e">E2E Tests</option>
              <option value="performance">Performance Tests</option>
              <option value="security">Security Tests</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="input-field"
              style={{ padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="all">All Status</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Environment</label>
            <select
              value={filters.environment}
              onChange={(e) => setFilters({ ...filters, environment: e.target.value })}
              className="input-field"
              style={{ padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="all">All Environments</option>
              <option value="local">Local</option>
              <option value="dev">Development</option>
              <option value="sit">SIT</option>
              <option value="uat">UAT</option>
              <option value="prod">Production</option>
            </select>
          </div>
        </div>
      </div>

      {/* Executions Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Execution ID</th>
              <th>Type</th>
              <th>Environment</th>
              <th>Status</th>
              <th>Tests</th>
              <th>Pass Rate</th>
              <th>Duration</th>
              <th>Git Commit</th>
              <th>Triggered By</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {executions.map((execution) => {
              const passRate = execution.total_tests > 0 
                ? ((execution.passed / execution.total_tests) * 100).toFixed(1) 
                : 0;
              
              return (
                <tr key={execution.execution_id} data-testid="execution-row">
                  <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>
                    <button
                      onClick={() => {
                        setSelectedExecution(execution);
                        setShowViewModal(true);
                      }}
                      style={{ background: 'none', border: 'none', color: '#002FA7', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 'inherit' }}
                      data-testid="execution-id-link"
                    >
                      {execution.execution_id}
                    </button>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{execution.test_type}</td>
                  <td style={{ textTransform: 'uppercase' }}>{execution.environment}</td>
                  <td>
                    <span className={`badge ${execution.status === 'passed' ? 'badge-low' : 'badge-critical'}`}>
                      {execution.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <span style={{ color: '#10B981', fontWeight: '600' }}>{execution.passed}</span>
                      {' / '}
                      <span style={{ color: '#EF4444', fontWeight: '600' }}>{execution.failed}</span>
                      {' / '}
                      <span style={{ color: '#6B7280' }}>{execution.skipped}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ fontWeight: '600', color: passRate >= 80 ? '#10B981' : passRate >= 50 ? '#F59E0B' : '#EF4444' }}>
                      {passRate}%
                    </span>
                  </td>
                  <td>{execution.duration_seconds.toFixed(2)}s</td>
                  <td>
                    {execution.git_commit_hash && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontFamily: 'IBM Plex Mono, monospace' }}>
                        <GitBranch size={14} />
                        {execution.git_commit_hash}
                      </div>
                    )}
                  </td>
                  <td>{execution.triggered_by_name}</td>
                  <td>{formatUtcToIstDate(execution.created_at)}</td>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedExecution(execution);
                        setShowViewModal(true);
                      }}
                      className="btn-secondary"
                      style={{ padding: '4px 12px', fontSize: '12px' }}
                      data-testid="view-execution-button"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        data-testid="create-execution-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Log Test Execution</h2>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateExecution}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Test Type</label>
                <select
                  value={formData.test_type}
                  onChange={(e) => setFormData({ ...formData, test_type: e.target.value })}
                  className="input-field"
                  data-testid="test-type-select"
                >
                  <option value="unit">Unit Tests</option>
                  <option value="integration">Integration Tests</option>
                  <option value="e2e">E2E Tests</option>
                  <option value="performance">Performance Tests</option>
                  <option value="security">Security Tests</option>
                </select>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Environment</label>
                <select
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                  className="input-field"
                  data-testid="environment-select"
                >
                  <option value="local">Local</option>
                  <option value="dev">Development</option>
                  <option value="sit">SIT</option>
                  <option value="uat">UAT</option>
                  <option value="prod">Production</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Total Tests</label>
                  <input
                    type="number"
                    value={formData.total_tests}
                    onChange={(e) => setFormData({ ...formData, total_tests: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                    min="0"
                    data-testid="total-tests-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Passed</label>
                  <input
                    type="number"
                    value={formData.passed}
                    onChange={(e) => setFormData({ ...formData, passed: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                    min="0"
                    data-testid="passed-input"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Failed</label>
                  <input
                    type="number"
                    value={formData.failed}
                    onChange={(e) => setFormData({ ...formData, failed: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                    min="0"
                    data-testid="failed-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Skipped</label>
                  <input
                    type="number"
                    value={formData.skipped}
                    onChange={(e) => setFormData({ ...formData, skipped: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                    min="0"
                    data-testid="skipped-input"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Duration (seconds)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.duration_seconds}
                  onChange={(e) => setFormData({ ...formData, duration_seconds: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  required
                  min="0"
                  data-testid="duration-input"
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="submit-execution-button">
                  Log Execution
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="cancel-execution-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedExecution && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        data-testid="view-execution-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>{selectedExecution.execution_id}</h2>
              <button onClick={() => setShowViewModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Test Type</p>
                <p style={{ fontSize: '16px', fontWeight: '600', textTransform: 'capitalize' }}>{selectedExecution.test_type}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Environment</p>
                <p style={{ fontSize: '16px', fontWeight: '600', textTransform: 'uppercase' }}>{selectedExecution.environment}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Status</p>
                <span className={`badge ${selectedExecution.status === 'passed' ? 'badge-low' : 'badge-critical'}`}>
                  {selectedExecution.status}
                </span>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Duration</p>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>{selectedExecution.duration_seconds.toFixed(2)} seconds</p>
              </div>
            </div>

            <div style={{ background: '#F9FAFB', padding: '10px', marginBottom: '8px', border: '1px solid #E5E7EB' }}>
              <h3 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Test Results Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>TOTAL</p>
                  <p style={{ fontSize: '15px', fontWeight: '700' }}>{selectedExecution.total_tests}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>PASSED</p>
                  <p style={{ fontSize: '15px', fontWeight: '700', color: '#10B981' }}>{selectedExecution.passed}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>FAILED</p>
                  <p style={{ fontSize: '15px', fontWeight: '700', color: '#EF4444' }}>{selectedExecution.failed}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>SKIPPED</p>
                  <p style={{ fontSize: '15px', fontWeight: '700', color: '#6B7280' }}>{selectedExecution.skipped}</p>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Triggered By</p>
              <p style={{ fontSize: '12px', fontWeight: '500' }}>{selectedExecution.triggered_by_name}</p>
            </div>

            {selectedExecution.git_commit_hash && (
              <div style={{ marginBottom: '8px' }}>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Git Commit (Test Branch)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px' }}>
                  <GitBranch size={16} />
                  {selectedExecution.git_commit_hash}
                </div>
              </div>
            )}

            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Executed At</p>
              <p style={{ fontSize: '12px', fontWeight: '500' }}>{formatUtcToIst(selectedExecution.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

export default TestExecutionDashboard;
